import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Bot } from "lucide-react";
import { MessageBubble } from "./MessageBubble";
import { LoadingBubble } from "./LoadingBubble";

export interface ChatMessage {
  id: string;
  role: string;
  content: string | null;
  createdAt: string;
  fileName?: string | null; // From server (stored file)
  file?: { name: string };  // For new messages being sent
  delegateUsed?: boolean;   // Ephemeral — set from delegate_used SSE event
}

interface MessageListProps {
  messages: ChatMessage[];
  isLoading?: boolean;
  isStreaming?: boolean;
}

export function MessageList({ messages, isLoading = false, isStreaming = false }: MessageListProps) {
  const { t } = useTranslation("common");
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center text-neutral-500">
          <Bot className="h-10 w-10 mx-auto mb-3 text-neutral-300" />
          <p className="text-sm font-medium">{t("chat.emptyTitle")}</p>
          <p className="text-xs mt-1">{t("chat.emptyDescription")}</p>
        </div>
      </div>
    );
  }

  // Check if last message is an empty assistant message (waiting for response)
  const lastMessage = messages[messages.length - 1];
  const showStreamingLoader = isStreaming && lastMessage?.role === "assistant" && !lastMessage?.content;

  // Filter messages to only show user and assistant messages with content
  const visibleMessages = messages.filter((message, index) => {
    if (message.role !== "user" && message.role !== "assistant") {
      return false;
    }
    // Skip assistant messages without content (tool calls)
    // Exception: show last empty assistant message only if we're streaming (will show loader)
    if (message.role === "assistant" && !message.content) {
      const isLast = index === messages.length - 1;
      return isLast && isStreaming;
    }
    return true;
  });

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      {visibleMessages.map((message, index) => {
        // Show loader instead of empty assistant message while streaming
        const isLastEmptyAssistant =
          index === visibleMessages.length - 1 && message.role === "assistant" && !message.content;
        if (isLastEmptyAssistant && showStreamingLoader) {
          return null;
        }
        return <MessageBubble key={message.id} message={message} />;
      })}
      {(isLoading || showStreamingLoader) && <LoadingBubble />}
      <div ref={bottomRef} />
    </div>
  );
}
